import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { CalendarCheck, MapPin, Phone, Clock } from "@phosphor-icons/react";

export default function InterviewConfirmPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/public/interview/${token}`).then((r) => setInfo(r.data)).catch(() => setErr("This interview link is invalid or no longer active."));
  }, [token]);

  const pretty = (() => {
    if (!info?.interview_at) return "";
    const m = info.interview_at.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/);
    if (!m) return info.interview_at;
    const d = new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:00`);
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) +
      " at " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  })();

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <div className="mx-auto max-w-xl px-6 py-10">
        <div className="mb-8 flex items-center gap-3">
          <img src="/shc-logo.png" alt="Silver Home Care" className="h-12 w-auto" />
          <div>
            <p className="font-heading text-xl font-bold tracking-tight text-stone-900">Silver Home Care</p>
            <p className="text-xs text-stone-500">Interview confirmation</p>
          </div>
        </div>

        {err && <p className="rounded-xl border border-[#C0392B]/30 bg-white p-8 text-center text-sm text-[#C0392B]" data-testid="interview-error">{err}</p>}

        {info && !err && (
          <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8" data-testid="interview-page">
            <div className="text-center">
              <CalendarCheck size={40} weight="fill" className="mx-auto text-primary" />
              <h1 className="mt-3 font-heading text-2xl font-bold text-stone-900">You have an interview, {info.name.split(" ")[0]}!</h1>
              <p className="mt-3 inline-block rounded-full bg-[#27AE60]/10 px-4 py-1.5 text-sm font-semibold text-[#1e8449]" data-testid="interview-when">{pretty || info.interview_at}</p>
            </div>

            <div className="mt-6 space-y-3 text-sm text-stone-700">
              <p className="flex gap-2.5"><MapPin size={18} className="mt-0.5 shrink-0 text-primary" />
                <span><b>Silver Home Care office</b><br />680 Lincoln Hwy, Fairless Hills, PA 19030</span></p>
              <p className="flex gap-2.5"><Clock size={18} className="mt-0.5 shrink-0 text-primary" />
                <span>Please <b>arrive on time</b>, and wear <b>casual, work-appropriate attire</b>.</span></p>
              <p className="flex gap-2.5"><Phone size={18} className="mt-0.5 shrink-0 text-primary" />
                <span>If you can't make it, please call us at <a href="tel:8007196912" className="font-semibold text-primary">800-719-6912</a> — <b>no-shows will not be rescheduled</b>.</span></p>
            </div>

            <div className="mt-6 rounded-xl bg-stone-50 p-4" data-testid="interview-instructions">
              <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">What to bring</p>
              {(info.docs_to_bring || []).length > 0 ? (
                <ul className="mt-2 space-y-1 text-xs leading-relaxed text-stone-600">
                  {info.docs_to_bring.map((d) => <li key={d} className="flex gap-1.5"><span className="text-primary">•</span>{d}</li>)}
                </ul>
              ) : (
                <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-stone-600">
                  Photo ID, Social Security card, and any documents you have — TB test, child abuse clearance, CNA registration, passport, or green card.
                </p>
              )}
            </div>

            <p className="mt-6 text-center text-xs text-stone-400">We look forward to meeting you. — Silver Home Care</p>
          </div>
        )}
      </div>
    </div>
  );
}
